/* eslint-disable react-native/no-inline-styles */
import React, { useState } from 'react';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import { isPhone, isWeb } from '@helpers';
import { Text, View } from 'react-native';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { font } from '@styles/fonts';
import { Element, scale } from '@components/frame';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import RadioButton from '@components/common/radioButton';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

const getData = () => {
  return [
    {
      name: i18n.t('Always light'),
      value: 'light',
      image: <Svg icon="themeLight" width={88} height={88} />,
    },
    {
      name: i18n.t('Follow System'),
      value: 'system',
      image: <Svg icon="themeSystem" width={88} height={88} />,
    },
    {
      name: i18n.t('Always dark'),
      value: 'dark',
      image: <Svg icon="themeDark" width={88} height={88} />,
    },
  ];
};

export const getThemeTileByValue = (value) => {
  const data = getData();

  const item = data.find((item) => item.value === value);
  if (item && item.name) {
    return item.name;
  } else {
    return 'System default';
  }
};

const ChangeThemeModal = (props) => {
  const {
    C,
    hide,
    maxHeight,
    onSubmit,
    submitLoading,
    loading,
    ColorsData: { updateCustom, custom },
    updateAppView,
  } = props;

  const headerHeight = scale(56);
  const selected = custom || 'system';
  const [isThemeChanging, setIsThemeChanging] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(custom || 'system'); // to track selected state right away after user click to display loader during update

  const [initialTheme] = useState(selected);
  const onSelect = async (item) => {
    trackEvent(TRACKING_EVENTS.SETTINGS, {
      action: 'themeChanged',
      oldTheme: initialTheme,
      newTheme: item.value,
    });
    setSelectedTheme(item.value);
    setIsThemeChanging(true);
    await updateCustom(item.value);
    updateAppView();
    setIsThemeChanging(false);
  };

  const onCancel = () => {
    updateCustom(initialTheme);
    updateAppView();
  };

  const data = getData();

  return (
    <View style={isWeb ? { backgroundColor: C('Background/Light/Field') } : {}}>
      <View
        style={{
          height: headerHeight,
          borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
          borderBottomWidth: isWeb ? 1 : 0,
          borderColor: C('Hairline/Light/Secondary'),
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 18,
                left: 14,
              },
              zIndex: 1000,
            },
          }}
        >
          <TouchableElement
            onPress={() => {
              onCancel();
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text
                  style={{
                    ...font.Body,
                    color: C(pressHoverColor('Link/Light/Special')),
                  }}
                >
                  {i18n.t('Cancel')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 18,
                right: 14,
              },
              zIndex: 1000,
            },
          }}
        >
          <TouchableElement
            onPress={async () => {
              if (submitLoading) {
                return;
              }

              if (onSubmit) {
                await onSubmit();
              }
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                {submitLoading ? (
                  <SmallLoading />
                ) : (
                  <Text
                    style={{
                      ...font.Headline,
                      color: C(pressHoverColor('Link/Light/Special')),
                    }}
                  >
                    {i18n.t('Done')}
                  </Text>
                )}
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              width: '100%',
              position: {
                top: 18,
              },
              justifyContent: 'center',
            },
          }}
        >
          <Text
            style={[
              {
                color: C('Label / Light / Primary'),
                textAlign: 'center',
              },
              font.Headline,
            ]}
          >
            {i18n.t('Dark / Light theme')}
          </Text>
        </Element>
      </View>
      <View style={{ maxHeight: maxHeight - headerHeight }}>
        {loading ? (
          <View style={{ width: '100%', height: scale(200), justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
            <SmallLoading />
          </View>
        ) : (
          <View
            style={[
              {
                paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
                paddingTop: scale(18),
                marginTop: scale(10),
                paddingBottom: isWeb ? scale(18) : scale(80),
              },
              !isWeb && {
                backgroundColor: C('Background/Light/Field'),
                width: '90%',
                marginLeft: 'auto',
                marginRight: 'auto',
                borderRadius: scale(10),
                marginTop: scale(25),
              },
              !isWeb &&
                !isPhone && {
                  paddingBottom: scale(10),
                  marginBottom: scale(56),
                },
            ]}
          >
            <View style={{ flexDirection: 'row' }}>
              {data.map((item, index) => {
                return (
                  <TouchableElement
                    key={`${item.value}-${index}`}
                    onPress={() => {
                      onSelect(item);
                    }}
                    hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  >
                    {({ boxStyle }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            flex: 1,
                            alignItems: 'center',
                            width: scale(88),
                          },
                        ]}
                      >
                        <View
                          style={{
                            marginBottom: scale(7),
                            borderRadius: 12,
                            borderWidth: 2,
                            borderColor: item.value === selected ? C('Selected/Light/Primary') : 'transparent',
                          }}
                        >
                          {item.image}
                        </View>
                        <Text
                          style={{
                            marginBottom: scale(6),
                            textAlign: 'center',
                            width: '50%',
                            color: item.value === selected ? C('Selected/Light/Primary') : C('Label/Light/Quinary'),
                          }}
                        >
                          {item.name}
                        </Text>
                        {selectedTheme === item.value && isThemeChanging ? (
                          <SmallLoading />
                        ) : (
                          <RadioButton {...{ C, type: 'dot', isSelected: item.value === selected, onSelect: () => onSelect(item) }} />
                        )}
                      </View>
                    )}
                  </TouchableElement>
                );
              })}
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default ColorWrapper(ChangeThemeModal);
